import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  MessageSquare,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Send,
  Maximize2,
  WifiOff,
  ChevronDown,
  AlertCircle,
  Paperclip,
  Image as ImageIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { useHotelHub } from "../../providers/signalr/HotelHubProvider";
import { useAdminNotificationStore } from "../../stores/adminNotificationStore";
import { useHotel } from "../../contexts/HotelContext";
import { useHotelTimezone } from "../../contexts/HotelTimezoneContext";
import { useMarkChatAsReadMutation } from "../../hooks/admin/queries/useChatQueries";
import { ChatConversationItem } from "./chat/ChatConversationItem";
import { RoomChatState, ActiveChatsMap } from "./chat/chatTypes";
import { ChatMessage, ChatMessageHistoryItem } from "../../models/ChatMessage";
import { logger } from "../../utils/logger";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

interface ActiveChatSession {
  roomId: string;
  actualRoomNumber?: string;
  lastMessageTimestamp?: string;
  unreadMessageCount?: number;
  lastMessageText?: string;
  lastMessageIsStaffMessage?: boolean;
  guestName?: string;
  checkInDate?: string;
  checkoutDate?: string;
}

interface ActiveChatSessionsPayload {
  hotelId?: string;
  sessions?: ActiveChatSession[];
  isAuthoritative?: boolean;
  errorCode?: string;
  requestId?: string;
}

const FloatingChatWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { on, off, invoke, isConnected } = useHotelHub();
  const { unreadChatCount, fetchUnreadChatCount } = useAdminNotificationStore();
  const { myHotelInfo } = useHotel();
  const hotelTimezone = useHotelTimezone();
  const markChatAsReadMutation = useMarkChatAsReadMutation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const markedAsReadRef = useRef<Set<string>>(new Set());
  const currentHotelIdRef = useRef(myHotelInfo?.hotelId);

  const [isOpen, setIsOpen] = useState(false);
  const [activeChats, setActiveChats] = useState<ActiveChatsMap>({});
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [totalConversations, setTotalConversations] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);

  const staffSenderName = t("adminChatPage.staffSenderName", "Staff");
  const isOnChatPage = location.pathname === "/admin/chat";
  const MAX_VISIBLE_CONVERSATIONS = 15;

  useEffect(() => {
    currentHotelIdRef.current = myHotelInfo?.hotelId;
    setActiveChats({});
    setSelectedRoomId(null);
    setTotalConversations(0);
    markedAsReadRef.current.clear();
  }, [myHotelInfo?.hotelId]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((immediate = false) => {
    if (immediate) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    setShowScrollButton(false);
  }, []);

  // Handle scroll position to show/hide scroll button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);
  }, []);

  // Auto-scroll when new messages arrive (only if already at bottom)
  useEffect(() => {
    if (selectedRoomId && !showScrollButton) {
      scrollToBottom();
    }
  }, [selectedRoomId, activeChats, scrollToBottom, showScrollButton]);

  // Focus input when opening a chat
  useEffect(() => {
    if (selectedRoomId && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [selectedRoomId]);

  // Keyboard escape to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        if (selectedRoomId) {
          handleBackToList();
        } else {
          handleClose();
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, selectedRoomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (date: Date): string => {
    return new Intl.DateTimeFormat("default", {
      timeZone: hotelTimezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  // Format date for message separators
  const formatMessageDate = (date: Date): string => {
    const fmt = (d: Date): string =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: hotelTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);

    const dateStr = fmt(date);
    const todayStr = fmt(new Date());

    if (dateStr === todayStr) {
      return t("floatingChat.today", "Today");
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateStr === fmt(yesterday)) {
      return t("floatingChat.yesterday", "Yesterday");
    }

    return new Intl.DateTimeFormat("default", {
      timeZone: hotelTimezone,
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Check if we need a date separator between messages
  const needsDateSeparator = (currentMsg: ChatMessage, prevMsg: ChatMessage | null): boolean => {
    if (!prevMsg) return true;
    const fmt = (d: Date): string =>
      new Intl.DateTimeFormat("en-CA", {
        timeZone: hotelTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    return fmt(currentMsg.timestampUtc) !== fmt(prevMsg.timestampUtc);
  };

  // Handle receiving active chat sessions from SignalR
  const handleActiveChatSessions = useCallback((payload: ActiveChatSessionsPayload | ActiveChatSession[]) => {
    const normalizedPayload: ActiveChatSessionsPayload = Array.isArray(payload)
      ? { sessions: payload }
      : payload;
    const expectedHotelId = currentHotelIdRef.current;
    const responseHotelId = normalizedPayload?.hotelId;

    if (normalizedPayload?.isAuthoritative === false) {
      logger.warn("FloatingChatWidget: Ignoring non-authoritative active chat sessions payload", {
        errorCode: normalizedPayload.errorCode,
        requestId: normalizedPayload.requestId,
        expectedHotelId,
        responseHotelId,
      });
      setIsLoadingChats(false);
      return;
    }

    if (
      !expectedHotelId ||
      !responseHotelId ||
      responseHotelId.toLowerCase() !== expectedHotelId.toLowerCase()
    ) {
      logger.warn("FloatingChatWidget: Ignoring active chat sessions for stale or missing hotel context", {
        expectedHotelId,
        responseHotelId,
      });
      setIsLoadingChats(false);
      return;
    }

    const sessions = normalizedPayload.sessions || [];

    logger.log("FloatingChatWidget: Received active chat sessions:", sessions.length);
    setIsLoadingChats(false);
    setTotalConversations(sessions.length);

    setActiveChats((prevChats) => {
      const newActiveChats: ActiveChatsMap = {};

      sessions.forEach((session) => {
        const lastMessageTime = session.lastMessageTimestamp
          ? new Date(session.lastMessageTimestamp)
          : undefined;
        const checkoutDate = session.checkoutDate ? new Date(session.checkoutDate) : undefined;

        // Preserve existing messages if we have them
        const existingChat = prevChats[session.roomId];
        const existingMessages = existingChat?.messages || [];

        const chatState: RoomChatState = {
          actualRoomNumber: session.actualRoomNumber,
          messages: existingMessages.length > 0 ? existingMessages : (session.lastMessageText && lastMessageTime ? [{
            senderName: session.lastMessageIsStaffMessage ? staffSenderName : "Guest",
            roomId: session.roomId,
            message: session.lastMessageText,
            timestampUtc: lastMessageTime,
            isStaffMessage: session.lastMessageIsStaffMessage || false,
          }] : []),
          unreadCount: session.unreadMessageCount || 0,
          guestName: session.guestName,
          checkoutDate,
        };

        newActiveChats[session.roomId] = chatState;
      });

      return newActiveChats;
    });
  }, [staffSenderName]);

  // Handle receiving message history
  const handleMessageHistory = useCallback((roomId: string, history: ChatMessageHistoryItem[]) => {
    logger.log("FloatingChatWidget: Received message history for room:", roomId, history?.length);
    setIsLoadingHistory(false);

    if (!history || history.length === 0) return;

    const messages: ChatMessage[] = history.map((item) => ({
      id: item.id,
      senderName: item.isStaffMessage ? staffSenderName : "Guest",
      roomId,
      message: item.message,
      timestampUtc: new Date(item.timestampUtc),
      isStaffMessage: item.isStaffMessage,
      attachmentUrl: item.attachmentUrl,
      attachmentType: item.attachmentType,
      attachmentFileName: item.attachmentFileName,
    }));

    setActiveChats((prevChats) => {
      const existingChat = prevChats[roomId] || { messages: [], unreadCount: 0 };
      return {
        ...prevChats,
        [roomId]: {
          ...existingChat,
          messages: messages.sort((a, b) => a.timestampUtc.getTime() - b.timestampUtc.getTime()),
        },
      };
    });
  }, [staffSenderName]);

  // Handle new message received
  const handleReceiveMessage = useCallback((payload: {
    senderName: string;
    roomId: string;
    message: string;
    isStaffMessage: boolean;
    id?: string;
    timestampUtc?: string;
    tempId?: string;
  }) => {
    logger.log("FloatingChatWidget: Received new message:", payload);

    const newMessage: ChatMessage = {
      id: payload.id,
      senderName: payload.senderName,
      roomId: payload.roomId,
      message: payload.message,
      timestampUtc: payload.timestampUtc ? new Date(payload.timestampUtc) : new Date(),
      isStaffMessage: payload.isStaffMessage,
    };

    setActiveChats((prevChats) => {
      const existingChat = prevChats[payload.roomId] || { messages: [], unreadCount: 0 };

      // Match optimistic message by tempId
      if (payload.tempId) {
        const optimisticIndex = existingChat.messages.findIndex(
          (m) => m.tempId && m.tempId === payload.tempId
        );
        if (optimisticIndex !== -1) {
          const updatedMessages = [...existingChat.messages];
          updatedMessages[optimisticIndex] = {
            ...updatedMessages[optimisticIndex],
            id: payload.id,
            deliveryStatus: 'delivered' as const,
            tempId: undefined,
          };
          return {
            ...prevChats,
            [payload.roomId]: { ...existingChat, messages: updatedMessages },
          };
        }
      }

      const messageExists = existingChat.messages.some(m => m.id && m.id === payload.id);

      if (messageExists) return prevChats;

      return {
        ...prevChats,
        [payload.roomId]: {
          ...existingChat,
          messages: [...existingChat.messages, newMessage].sort(
            (a, b) => a.timestampUtc.getTime() - b.timestampUtc.getTime()
          ),
          unreadCount: payload.isStaffMessage ? existingChat.unreadCount : (existingChat.unreadCount || 0) + 1,
        },
      };
    });

    // Refresh unread count
    fetchUnreadChatCount();

    // Refresh conversation list if widget is open (require hotelId to prevent
    // server falling back to first-owned hotel for multi-hotel staff)
    if (isOpen && isConnected) {
      const hotelId = myHotelInfo?.hotelId;
      if (hotelId) {
        invoke("GetAllActiveChatSessions", hotelId).catch(() => {});
      }
    }
  }, [fetchUnreadChatCount, isOpen, isConnected, invoke, myHotelInfo?.hotelId]);

  // Set up SignalR event handlers
  useEffect(() => {
    if (!isConnected || !isAuthenticated) return;

    on("ReceiveActiveChatSessions", handleActiveChatSessions);
    on("ReceiveMessage", handleReceiveMessage);
    on("ReceiveMessageWithReply", handleReceiveMessage);
    on("ReceiveMessageHistory", handleMessageHistory);

    return () => {
      off("ReceiveActiveChatSessions", handleActiveChatSessions);
      off("ReceiveMessage", handleReceiveMessage);
      off("ReceiveMessageWithReply", handleReceiveMessage);
      off("ReceiveMessageHistory", handleMessageHistory);
    };
  }, [isConnected, isAuthenticated, on, off, handleActiveChatSessions, handleReceiveMessage, handleMessageHistory]);

  // Request active sessions when widget opens (wait for hotelId to avoid the
  // server falling back to the user's first-owned hotel)
  useEffect(() => {
    if (isOpen && isConnected && isAuthenticated && !selectedRoomId) {
      const hotelId = myHotelInfo?.hotelId;
      if (!hotelId) return;
      setIsLoadingChats(true);
      invoke("GetAllActiveChatSessions", hotelId).catch((err) => {
        logger.error("FloatingChatWidget: Failed to get active chat sessions:", err);
        setIsLoadingChats(false);
      });
    }
  }, [isOpen, isConnected, isAuthenticated, invoke, myHotelInfo?.hotelId, selectedRoomId]);

  // Load chat history when a conversation is selected
  useEffect(() => {
    const hotelId = myHotelInfo?.hotelId;
    if (selectedRoomId && isConnected && hotelId) {
      setIsLoadingHistory(true);
      invoke("JoinRoomChat", selectedRoomId, hotelId)
        .then(() => invoke("LoadRoomHistory", selectedRoomId, hotelId))
        .catch((err) => {
          logger.error("FloatingChatWidget: Failed to get chat history:", err);
          setIsLoadingHistory(false);
        });

      // Mark as read only once per room selection
      if (!markedAsReadRef.current.has(selectedRoomId)) {
        markedAsReadRef.current.add(selectedRoomId);
        markChatAsReadMutation.mutate({ roomId: selectedRoomId });
      }

      // Clear unread count locally
      setActiveChats((prevChats) => {
        const chat = prevChats[selectedRoomId];
        if (chat) {
          return {
            ...prevChats,
            [selectedRoomId]: { ...chat, unreadCount: 0 },
          };
        }
        return prevChats;
      });
    }
  }, [selectedRoomId, isConnected, invoke, myHotelInfo?.hotelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send message
  const sendMessage = async () => {
    if (!currentMessage.trim() || !selectedRoomId || isSending) return;

    const hotelId = myHotelInfo?.hotelId;
    if (!hotelId) {
      setSendError(t("floatingChat.hotelContextMissing", "Unable to determine selected hotel"));
      return;
    }

    // Check connection before sending
    if (!isConnected) {
      setSendError(t("floatingChat.notConnected", "Not connected. Please wait..."));
      return;
    }

    const messageText = currentMessage.trim();
    setIsSending(true);
    setSendError(null);

    // Optimistic update
    const msgTempId = crypto.randomUUID();
    const tempMessage: ChatMessage = {
      tempId: msgTempId,
      senderName: staffSenderName,
      roomId: selectedRoomId,
      message: messageText,
      timestampUtc: new Date(),
      isStaffMessage: true,
      deliveryStatus: 'sending',
    };

    setActiveChats((prevChats) => {
      const chat = prevChats[selectedRoomId] || { messages: [], unreadCount: 0 };
      return {
        ...prevChats,
        [selectedRoomId]: {
          ...chat,
          messages: [...chat.messages, tempMessage].sort(
            (a, b) => a.timestampUtc.getTime() - b.timestampUtc.getTime()
          ),
        },
      };
    });

    setCurrentMessage("");

    try {
      await invoke("SendMessage", staffSenderName, selectedRoomId, messageText, true, null, msgTempId, hotelId);
      // Scroll to bottom after successful send
      setTimeout(() => scrollToBottom(), 50);
    } catch (err) {
      logger.error("FloatingChatWidget: Failed to send message:", err);
      setSendError(t("floatingChat.sendFailed", "Failed to send. Tap to retry."));
      // Remove optimistic message on error
      setActiveChats((prevChats) => {
        const chat = prevChats[selectedRoomId];
        if (chat) {
          return {
            ...prevChats,
            [selectedRoomId]: {
              ...chat,
              messages: chat.messages.filter((m) => m.tempId !== msgTempId),
            },
          };
        }
        return prevChats;
      });
      // Restore the message so user can retry
      setCurrentMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const handleSelectConversation = (roomId: string) => {
    setSelectedRoomId(roomId);
  };

  const handleBackToList = () => {
    // Clear marked-as-read so re-opening will mark again if new messages arrived
    if (selectedRoomId) {
      markedAsReadRef.current.delete(selectedRoomId);
    }
    setSelectedRoomId(null);
  };

  const handleClose = () => {
    markedAsReadRef.current.clear();
    setSelectedRoomId(null);
    setIsOpen(false);
  };

  const handleOpenFullChat = () => {
    const roomToOpen = selectedRoomId;
    handleClose();
    if (roomToOpen) {
      navigate(`/admin/chat?room=${roomToOpen}`);
    } else {
      navigate("/admin/chat");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isOnChatPage) {
    return null;
  }

  const selectedChat = selectedRoomId ? activeChats[selectedRoomId] : null;

  const sortedConversations = Object.entries(activeChats)
    .map(([roomId, chat]) => ({ roomId, chat }))
    .sort((a, b) => {
      const aUnread = a.chat.unreadCount || 0;
      const bUnread = b.chat.unreadCount || 0;
      if (aUnread > 0 && bUnread === 0) return -1;
      if (bUnread > 0 && aUnread === 0) return 1;
      const aTime = a.chat.messages[a.chat.messages.length - 1]?.timestampUtc?.getTime() || 0;
      const bTime = b.chat.messages[b.chat.messages.length - 1]?.timestampUtc?.getTime() || 0;
      return bTime - aTime;
    })
    .slice(0, MAX_VISIBLE_CONVERSATIONS);

  const hiddenConversationsCount = totalConversations > MAX_VISIBLE_CONVERSATIONS
    ? totalConversations - MAX_VISIBLE_CONVERSATIONS
    : 0;

  return (
    <TooltipProvider delayDuration={300}>
      {/* Responsive positioning: smaller margin on small screens */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50" ref={widgetRef}>
        {isOpen && (
          <div
            className={cn(
              "absolute bottom-16 right-0",
              // Responsive width: full width minus margins on very small screens
              "w-[calc(100vw-2rem)] sm:w-96",
              // Responsive height: account for mobile viewport
              "h-[min(500px,calc(100vh-8rem))]",
              // Max dimensions for large screens
              "max-w-md",
              "bg-background border rounded-lg shadow-xl overflow-hidden",
              "animate-in slide-in-from-bottom-2 duration-200 flex flex-col"
            )}
            role="dialog"
            aria-label={t("floatingChat.title", "Live Chat")}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-primary text-primary-foreground shrink-0">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {selectedRoomId && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-primary-foreground hover:bg-primary-foreground/20 shrink-0"
                        onClick={handleBackToList}
                        aria-label={t("floatingChat.backToList", "Back to conversations")}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {t("floatingChat.backToList", "Back to conversations")}
                    </TooltipContent>
                  </Tooltip>
                )}
                <MessageSquare className="h-5 w-5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <span className="font-medium truncate block">
                    {selectedChat
                      ? `${t("floatingChat.room", "Room")} ${selectedChat.actualRoomNumber || selectedRoomId}`
                      : t("floatingChat.title", "Live Chat")}
                  </span>
                  {/* Guest name in chat view */}
                  {selectedChat?.guestName && (
                    <span className="text-xs text-primary-foreground/70 truncate block">
                      {selectedChat.guestName}
                    </span>
                  )}
                </div>
                {!selectedRoomId && unreadChatCount > 0 && (
                  <span className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full shrink-0">
                    {unreadChatCount > 99 ? "99+" : unreadChatCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                {/* Connection status indicator */}
                {!isConnected && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="h-7 w-7 flex items-center justify-center text-destructive-foreground/80">
                        <WifiOff className="h-4 w-4" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {t("floatingChat.disconnected", "Disconnected - Reconnecting...")}
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                      onClick={handleOpenFullChat}
                      aria-label={t("floatingChat.openFullChat", "Open full chat")}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t("floatingChat.openFullChat", "Open full chat")}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-primary-foreground hover:bg-primary-foreground/20"
                      onClick={handleClose}
                      aria-label={t("floatingChat.close", "Close")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t("floatingChat.close", "Close")} (Esc)
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Content */}
            {selectedRoomId ? (
              // Chat View
              <>
                <div
                  ref={messagesContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto p-3 space-y-1 relative"
                >
                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : selectedChat?.messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm gap-2">
                      <MessageSquare className="h-8 w-8 opacity-50" />
                      <p>{t("floatingChat.noMessages", "No messages yet")}</p>
                      <p className="text-xs">{t("floatingChat.startConversation", "Send a message to start the conversation")}</p>
                    </div>
                  ) : (
                    <>
                      {selectedChat?.messages.map((msg, idx) => {
                        const prevMsg = idx > 0 ? selectedChat.messages[idx - 1] : null;
                        const showDateSeparator = needsDateSeparator(msg, prevMsg);

                        return (
                          <React.Fragment key={msg.id || idx}>
                            {/* Date separator */}
                            {showDateSeparator && (
                              <div className="flex items-center justify-center my-3">
                                <div className="bg-muted text-muted-foreground text-xs px-3 py-1 rounded-full">
                                  {formatMessageDate(msg.timestampUtc)}
                                </div>
                              </div>
                            )}
                            <div
                              className={cn(
                                "flex",
                                msg.isStaffMessage ? "justify-end" : "justify-start"
                              )}
                            >
                              <div
                                className={cn(
                                  "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                                  msg.isStaffMessage
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted"
                                )}
                              >
                                {/* Attachment indicator */}
                                {msg.attachmentUrl && (
                                  <div className={cn(
                                    "flex items-center gap-1 text-xs mb-1",
                                    msg.isStaffMessage ? "text-primary-foreground/80" : "text-muted-foreground"
                                  )}>
                                    {msg.attachmentType?.startsWith("image/") ? (
                                      <ImageIcon className="h-3 w-3" />
                                    ) : (
                                      <Paperclip className="h-3 w-3" />
                                    )}
                                    <span className="truncate">
                                      {msg.attachmentFileName || t("floatingChat.attachment", "Attachment")}
                                    </span>
                                  </div>
                                )}
                                <p className="wrap-break-word whitespace-pre-wrap">{msg.message}</p>
                                <p className={cn(
                                  "text-[10px] mt-1",
                                  msg.isStaffMessage ? "text-primary-foreground/60" : "text-muted-foreground"
                                )}>
                                  {formatTime(msg.timestampUtc)}
                                </p>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}

                  {/* Scroll to bottom button */}
                  {showScrollButton && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="absolute bottom-2 left-1/2 -translate-x-1/2 h-8 px-3 shadow-lg rounded-full"
                      onClick={() => scrollToBottom()}
                    >
                      <ChevronDown className="h-4 w-4 mr-1" />
                      {t("floatingChat.scrollToBottom", "New messages")}
                    </Button>
                  )}
                </div>

                {/* Error message */}
                {sendError && (
                  <div
                    className="px-3 py-2 bg-destructive/10 text-destructive text-xs flex items-center gap-2 cursor-pointer hover:bg-destructive/20"
                    onClick={() => {
                      setSendError(null);
                      sendMessage();
                    }}
                  >
                    <AlertCircle className="h-3 w-3 shrink-0" />
                    <span>{sendError}</span>
                  </div>
                )}

                {/* Message Input */}
                <div className="border-t p-2 shrink-0">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={currentMessage}
                      onChange={(e) => {
                        setCurrentMessage(e.target.value);
                        setSendError(null);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder={t("floatingChat.typeMessage", "Type a message...")}
                      disabled={isSending}
                      className="flex-1"
                      maxLength={2000}
                      aria-label={t("floatingChat.typeMessage", "Type a message...")}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          onClick={sendMessage}
                          disabled={!currentMessage.trim() || isSending}
                          aria-label={t("floatingChat.send", "Send message")}
                        >
                          {isSending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {t("floatingChat.send", "Send")} (Enter)
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  {/* Character count for long messages */}
                  {currentMessage.length > 1800 && (
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {currentMessage.length}/2000
                    </p>
                  )}
                </div>
              </>
            ) : (
              // Conversation List
              <>
                <div className="flex-1 overflow-y-auto">
                  {isLoadingChats ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : sortedConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 px-4 text-muted-foreground text-sm gap-2">
                      <MessageSquare className="h-8 w-8 opacity-50" />
                      <p>{t("floatingChat.noConversations", "No active conversations")}</p>
                      <p className="text-xs text-center">
                        {t("floatingChat.noConversationsHint", "Conversations will appear when guests send messages")}
                      </p>
                    </div>
                  ) : (
                    <>
                      {sortedConversations.map(({ roomId, chat }) => (
                        <ChatConversationItem
                          key={roomId}
                          roomId={roomId}
                          chat={chat}
                          isSelected={false}
                          onSelect={handleSelectConversation}
                          formatTime={formatTime}
                          hasUnread={(chat.unreadCount || 0) > 0}
                        />
                      ))}
                      {/* Show count of hidden conversations */}
                      {hiddenConversationsCount > 0 && (
                        <div className="px-3 py-2 text-center text-xs text-muted-foreground border-t">
                          {t("floatingChat.moreConversations", "+{{count}} more conversations", { count: hiddenConversationsCount })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t p-2 shrink-0">
                  <Button
                    variant="ghost"
                    className="w-full justify-between text-sm"
                    onClick={handleOpenFullChat}
                  >
                    <span>
                      {totalConversations > 0
                        ? t("floatingChat.viewAllConversations", "View all {{count}} conversations", { count: totalConversations })
                        : t("floatingChat.openFullChat", "Open full chat")}
                    </span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Floating Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="lg"
              className={cn(
                "h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg",
                "hover:scale-105 transition-transform duration-200",
                isOpen && "ring-2 ring-primary ring-offset-2",
                !isConnected && "opacity-80"
              )}
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen
                ? t("floatingChat.closeChat", "Close chat")
                : t("floatingChat.openChat", "Open chat")}
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
              {unreadChatCount > 0 && !isOpen && (
                <span className={cn(
                  "absolute -top-1 -right-1 flex items-center justify-center",
                  "min-w-[20px] h-5 px-1 rounded-full",
                  "bg-destructive text-destructive-foreground text-xs font-semibold",
                  "animate-in zoom-in-50 duration-200"
                )}>
                  {unreadChatCount > 99 ? "99+" : unreadChatCount}
                </span>
              )}
              {/* Connection indicator on button */}
              {!isConnected && !isOpen && (
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-orange-500 rounded-full border-2 border-background" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            {isOpen
              ? t("floatingChat.closeChat", "Close chat")
              : unreadChatCount > 0
                ? t("floatingChat.unreadMessages", "{{count}} unread messages", { count: unreadChatCount })
                : t("floatingChat.openChat", "Open chat")}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export default FloatingChatWidget;
